import dayjs from "dayjs";
import { cloneDeep, get, has, isEmpty, isEqual, set } from "lodash-es";
import { defineStore } from "pinia";
import { useRoute } from "vue-router";

import {
  getHotelRatesAsync,
  getHotels,
  getHotelsAsync
} from "@/api/biz/openHotel";
import {
  clearUserRecentHistory,
  getUserRecentHistory,
  setUserRecentHistory
} from "@/api/biz/user";
import { useToggles } from "@/hooks/useToggles";
import { EHotelRateSortType } from "@/share/hotel";
import { store } from "@/store";
import { useUserStoreHook } from "@/store/modules/user";
import {
  addLocalHistoryItem,
  clearLocalHistory,
  getLocalHistory
} from "@/utils/history";
import { queryToParams } from "@/utils/tool";

import { mergeRateFilterNodes } from "../utils/hotelRateFilter";

export const useHotelStore = defineStore({
  id: "y-hotel",
  state: () => ({
    // 酒店/房型搜索公用参数参数
    searchModel: {
      stay: {
        date: {
          checkIn: dayjs().format("YYYY-MM-DD"),
          checkOut: dayjs().add(1, "day").format("YYYY-MM-DD")
        },
        guest: {
          numberOfRooms: 1,
          numberOfAdults: 2,
          numberOfChildren: 0
        }
      }
    },
    // 酒店搜索参数
    hotelSearchModel: {
      filter: {},
      sort: "byRecommendation",
      location: {},
      commissionPromotion: "", // 是否筛选高佣
      locationRef: "", // locationRef
      locationType: "city", // location类型 city/modelTpHotel/googleMapPolitical
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        total: 0
      }
    },
    /** 选中的酒店 */
    selectedHotel: null,
    /** 选中的房间 */
    selectedRoom: null,
    /** 选中的rate */
    selectedRate: null,

    /**分割线 上面是搜索参数相关，下面是数据展示 */

    /** 酒店列表 */
    hotelList: [],
    /** 历史记录 */
    historyList: [],
    /** 酒店房型列表 */
    roomList: [],
    /** 酒店详情的状态 */
    hotelDetail: {
      isLoading: true,
      isLoaded: false
    },
    hotelInitLoading: true, // 酒店列表初始化的loading
    hotelListInitialLoadDone: false,
    roomListInitLoading: false, // 房型列表初始化的loading
    roomListInitLoadingDone: false, //房型列表初始化的loading是否完成
    hotelListLoadingMore: false, // 酒店列表加载更多loading
    roomListLoadingMore: false, // 房型列表加载更多loading
    hotelListNoMore: false, // 酒店列表没有更多了
    hotelListSearchSnapshot: null,
    hotelListRequestToken: 0,
    extraBrands: [], // 额外的品牌
    extraTags: null, // 额外的促销、权益和酒店标签
    showCommission: true, // 是否显示佣金
    /** rate 可筛选项（`_biz.rateFilter`，Y_RATE_AI_FEATURE_FILTER 开启时才有） */
    rateFilter: null,
    /** 本轮查询 asyncJob 分片累积出来的选项（分片只含自己那部分 room 的选项，需要并集） */
    rateFilterAccumulator: null,
    /** 本轮查询的 asyncJob 链是否结束（只有结束时才按「选项是否仍存在」裁剪已选） */
    rateFilterComplete: false,
    /** 当前 rateFilter / 已选所属的酒店（换酒店时用于立刻清空上一家的标签与选中态） */
    rateFilterHotelId: null,
    /** 房型列表请求令牌：换酒店/换日期时只认最后一次请求的响应 */
    roomListRequestToken: 0,
    /** rate 筛选选中态：标签（分组内单选、分组间交集）、排序、多晚总价 */
    rateFilterSelected: {
      tags: [],
      sortTypes: [EHotelRateSortType.BY_BENEFIT],
      multiNightTotal: true
    }
  }),
  actions: {
    SET_SEARCH_MODEL(model) {
      this.searchModel = { ...this.searchModel, ...model };
    },
    SET_HOTEL_SEARCH_MODEL(model) {
      this.hotelSearchModel = { ...this.hotelSearchModel, ...model };
    },
    SET_SELECTED_HOTEL(hotel) {
      this.selectedHotel = hotel;
    },
    SET_SELECTED_ROOM(room) {
      this.selectedRoom = room;
    },
    SET_SELECTED_RATE(rate) {
      this.selectedRate = rate;
    },
    SET_RATE_FILTER(rateFilter) {
      this.rateFilter = rateFilter || null;
    },
    /**
     * asyncJob 分片：把该片的选项并入累积结果后再发布，
     * 并记录整条链是否结束（最后一片不带 asyncJobId）
     */
    SET_RATE_FILTER_CHUNK({ rateFilter, complete } = {}) {
      if (rateFilter) {
        this.rateFilterAccumulator = mergeRateFilterNodes(
          this.rateFilterAccumulator,
          rateFilter
        );
        this.rateFilter = this.rateFilterAccumulator;
      }
      this.rateFilterComplete = Boolean(complete);
    },
    /**
     * 换酒店：立刻清掉上一家酒店的筛选选项与已选。
     * 必须在"感知到换酒店的第一时间"调用（进详情页 / 路由变化时），
     * 不能等到房型请求 —— 否则 hotel 信息加载那段时间页面上还挂着上一家的标签。
     */
    CLEAR_RATE_FILTER_STATE() {
      this.rateFilter = null;
      this.rateFilterHotelId = null;
      this.rateFilterAccumulator = null;
      this.rateFilterComplete = false;
      this.SET_RATE_FILTER_SELECTED({ tags: [] });
    },
    /** 新一轮查询开始：清掉分片累积（保留 rateFilter，避免界面上的已选项闪一下） */
    RESET_RATE_FILTER_CHAIN() {
      this.rateFilterAccumulator = null;
      this.rateFilterComplete = false;
    },
    SET_RATE_FILTER_SELECTED(selected = {}) {
      this.rateFilterSelected = { ...this.rateFilterSelected, ...selected };
    },
    setRatePriceDetail(priceDetail) {
      if (this.selectedRate) {
        this.selectedRate.priceDetail = priceDetail;
      } else {
        console.warn("没有房型，无法设置价格");
      }
    },
    /**
     * 初始化 Store
     * @param {Object} query - URL 查询参数
     * @param {Boolean} force - 是否强制重置
     */
    initialize(query = {}, force = false) {
      const params = queryToParams(query);
      this.initializeHotelSearchModelFromParams(params);
      if (force) {
        this.resetPagination();
        this.hotelList = [];
        this.hotelListNoMore = false;
        this.hotelListInitialLoadDone = false;
      }
    },
    initializeHotelSearchModelFromParams(params) {
      if (params.stay) {
        if (params.stay.date) {
          this.searchModel.stay.date.checkIn = params.stay.date.checkIn;
          this.searchModel.stay.date.checkOut = params.stay.date.checkOut;
        }
        if (params.stay.guest) {
          this.searchModel.stay.guest.numberOfRooms = Number(
            params.stay.guest.numberOfRooms
          );
          this.searchModel.stay.guest.numberOfAdults = Number(
            params.stay.guest.numberOfAdults
          );
          this.searchModel.stay.guest.numberOfChildren = Number(
            params.stay.guest.numberOfChildren || 0
          );
        }
      }

      if (params.location) {
        this.hotelSearchModel.locationRef = params.location._id;
        this.hotelSearchModel.locationType = params.location.locationType;

        if (params.location.showLabel) {
          this.hotelSearchModel.location = params.location;
        }
      } else {
        this.hotelSearchModel.locationRef = "";
        this.hotelSearchModel.locationType = "";
        this.hotelSearchModel.location = null;
      }

      if (params.filter) {
        this.hotelSearchModel.filter = params.filter;
      }

      if (params.sort) {
        this.hotelSearchModel.sort = params.sort;
      }
    },
    resetPagination() {
      this.hotelSearchModel.pagination = {
        pageIndex: 1,
        pageSize: 10,
        total: 0
      };
    },
    updatePagination(totalCount) {
      this.hotelSearchModel.pagination.pageIndex += 1;
      this.hotelSearchModel.pagination.total = totalCount;
    },
    getHotelListSearchSnapshot() {
      const userStore = useUserStoreHook();

      return {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: cloneDeep(this.searchModel.stay),
        filter: cloneDeep(this.hotelSearchModel.filter),
        sort: this.hotelSearchModel.sort,
        commissionPromotion: this.hotelSearchModel.commissionPromotion,
        locationRef: this.hotelSearchModel.locationRef,
        locationType: this.hotelSearchModel.locationType
      };
    },
    async initHotelList() {
      if (this.hotelListLoadingMore) return;

      const searchSnapshot = this.getHotelListSearchSnapshot();
      let requestToken = this.hotelListRequestToken;

      if (this.hotelSearchModel.pagination.pageIndex === 1) {
        this.hotelList = [];
        this.hotelListNoMore = false;
        this.hotelInitLoading = true;
        this.hotelListInitialLoadDone = false;
        this.hotelListSearchSnapshot = cloneDeep(searchSnapshot);
        this.hotelListRequestToken += 1;
        requestToken = this.hotelListRequestToken;
      }

      const isCurrentRequest = () => {
        return (
          requestToken === this.hotelListRequestToken &&
          isEqual(this.hotelListSearchSnapshot, searchSnapshot)
        );
      };

      if (
        this.hotelSearchModel.pagination.total > 0 &&
        this.hotelList.length >= this.hotelSearchModel.pagination.total
      ) {
        this.hotelListNoMore = true;
        this.hotelInitLoading = false;
        this.hotelListLoadingMore = false;
        return;
      }

      if (this.hotelList.length > 0) {
        this.hotelListLoadingMore = true;
      }
      const isPriceSort = [
        "byMinBasePrice",
        "byMinBasePriceDescending"
      ].includes(this.hotelSearchModel.sort);
      const userStore = useUserStoreHook();
      const params = {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: this.searchModel.stay,
        filter: cloneDeep(this.hotelSearchModel.filter),
        paging: {
          limit: this.hotelSearchModel.pagination.pageSize,
          skip:
            (this.hotelSearchModel.pagination.pageIndex - 1) *
            this.hotelSearchModel.pagination.pageSize
        },
        sort: this.hotelSearchModel.sort,
        // 筛选选项（品牌/促销/礼遇/酒店标签）改由下面单独的 optionsParams 请求获取，
        // 主请求只关心酒店列表，避免多算一次聚合
        options: {
          extraBrandsRequired: false,
          extraTagsRequired: false
        }
      };

      if (this.hotelSearchModel.commissionPromotion === "highCommission") {
        set(params, "filter._biz.commissionPromotion", true);
      } else {
        if (has(params, "filter._biz.commissionPromotion")) {
          if (isEmpty(params.filter._biz)) {
            delete params.filter._biz;
          }
        }
      }

      if (this.$router) {
        const query = this.$router.currentRoute.query;
        const paramsFromUrl = queryToParams(query);

        if (!isEmpty(paramsFromUrl.filter)) {
          params.filter = { ...paramsFromUrl.filter, ...params.filter };
        }
        if (paramsFromUrl.options) {
          params.options = { ...params.options, ...paramsFromUrl.options };
        }
      }

      // 筛选选项单独请求，且必须剔除「已经选中的筛选条件」：
      // 否则选项列表会被筛选后的酒店集合反向过滤（选中一个标签后其它标签消失），
      // 与移动端（选中后不再重新拉取选项）保持一致。
      // 选项只跟搜索范围（城市/日期/关键词/地图区域）相关，翻页时无需重复请求，
      // 与上面「pageIndex === 1 才算新查询」的判断保持一致。
      const isFirstPage = this.hotelSearchModel.pagination.pageIndex === 1;
      if (isFirstPage) {
        const optionFilterKeys = [
          "brands",
          "promotions",
          "benefits",
          "hotelTags"
        ];
        const optionsParams = cloneDeep(params);
        optionFilterKeys.forEach(key => {
          delete optionsParams.filter[key];
        });
        // 只取 extra：排序固定为推荐、分页降到最小，避免为选项额外触发比价
        optionsParams.options = {
          extraBrandsRequired: true,
          extraTagsRequired: true
        };
        optionsParams.sort = "byRecommendation";
        optionsParams.paging = { limit: 1, skip: 0 };
        getHotelsAsync(optionsParams, res => {
          this.extraBrands = get(res, "extra.brands", this.extraBrands);
          this.extraTags = get(res, "extra", this.extraTags);
        });
      }

      getHotelsAsync(
        params,
        res => {
          if (!isCurrentRequest()) return;

          const hotelsData = res.hotels || [];
          if (hotelsData.length === 0 && !res.asyncJobId) {
            this.hotelInitLoading = false;
            this.hotelListLoadingMore = false;
            this.hotelSearchModel.pagination.total = this.hotelList.length;
            this.hotelListNoMore = true;
            return;
          }

          const incomingIds = hotelsData.map(h => h._id);
          const allExist = incomingIds.every(id =>
            this.hotelList.some(h => h._id === id)
          );
          const isUpdateOperation =
            this.hotelList.length > 0 && hotelsData.length > 0 && allExist;

          if (isUpdateOperation) {
            hotelsData.forEach(asyncItem => {
              const index = this.hotelList.findIndex(
                h => h._id === asyncItem._id
              );
              if (index !== -1) {
                this.hotelList[index] = {
                  ...this.hotelList[index],
                  ...asyncItem,
                  _biz: {
                    ...this.hotelList[index]._biz,
                    ...asyncItem._biz,
                    commissionPromotion: Boolean(
                      get(asyncItem, "_biz.commissionPromotion", false)
                    )
                  }
                };
              }
            });
          } else {
            const uniqueHotels = hotelsData.filter(
              newItem => !this.hotelList.some(h => h._id === newItem._id)
            );
            this.hotelList = [...this.hotelList, ...uniqueHotels];

            this.updatePagination(res.paging?.count || this.hotelList.length);
          }

          this.hotelInitLoading = false;
          this.hotelListLoadingMore = false;

          const total = this.hotelSearchModel.pagination.total;
          this.hotelListNoMore = this.hotelList.length >= total;

          if (!this.hotelListInitialLoadDone) {
            this.hotelListInitialLoadDone = true;
          }
        },
        () => {
          if (!isCurrentRequest()) return;

          // 错误处理
          this.hotelInitLoading = false;
          this.hotelListLoadingMore = false;
          if (!this.hotelListInitialLoadDone) {
            this.hotelListInitialLoadDone = true;
          }
        },
        isPriceSort ? { timeout: 180000 } : undefined
      );
    },

    async initHotelDetail() {
      const userStore = useUserStoreHook();
      // 换酒店：这里是最早能确定的点，先清掉上一家的筛选项，避免 hotel 加载期间还显示上一家标签
      const _queryHotelId = useRoute()?.query?.hotelId;
      if (
        _queryHotelId &&
        this.rateFilterHotelId &&
        this.rateFilterHotelId !== _queryHotelId
      ) {
        this.CLEAR_RATE_FILTER_STATE();
      }
      this.roomList = [];
      this.roomListInitLoading = false;
      this.roomListInitLoadingDone = false;
      this.hotelDetail.isLoading = true;
      this.hotelDetail.isLoaded = false;
      const route = useRoute();
      let _hotelId = route?.query?.hotelId;
      if (!_hotelId && !isEmpty(this.selectedHotel)) {
        _hotelId = this.selectedHotel?._id;
      }
      this.selectedHotel = null;
      const params = {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: this.searchModel.stay,
        filter: {
          hotels: [{ _id: _hotelId }]
        },
        paging: { limit: 1, skip: 0 }
      };

      try {
        const res = await getHotels(params);
        if (res.success && res.hotels) {
          this.selectedHotel = res.hotels[0];
          this.hotelDetail.isLoaded = true;
          this.hotelDetail.isLoading = false;
        }
      } catch (error) {
        console.error("initHotelDetail 出错", error);
      } finally {
        this.hotelDetail.isLoading = false;
      }
      this.initRoomList();
    },
    async initHotelDetailById(hotelId) {
      if (!hotelId) {
        console.error(" requires a hotelId.");
        return;
      }

      this.hotelDetail.isLoading = true;
      this.hotelDetail.isLoaded = false;
      const userStore = useUserStoreHook();

      const params = {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: this.searchModel.stay,
        filter: {
          hotels: [{ _id: hotelId }]
        },
        paging: { limit: 1, skip: 0 }
      };

      try {
        const res = await getHotels(params);
        if (res.success && res.hotels && res.hotels.length > 0) {
          this.selectedHotel = res.hotels[0];
          this.hotelDetail.isLoaded = true;
        } else {
          this.selectedHotel = null;
        }
      } catch (error) {
        console.error(`initHotelDetailById:`, error);
      } finally {
        this.hotelDetail.isLoading = false;
      }
    },
    async initRoomList() {
      const userStore = useUserStoreHook();
      const route = useRoute();
      let _hotelId = route?.query?.hotelId;
      if (!_hotelId && !isEmpty(this.selectedHotel)) {
        _hotelId = this.selectedHotel?._id;
      }

      if (!_hotelId) {
        this.roomListInitLoading = false;
        this.roomListInitLoadingDone = true;
        return;
      }

      // 换酒店：上一家的选项和选中态立刻清掉。
      // 注意必须在任何"提前 return"之前做 —— 否则上一家的请求还在途时切到新酒店，
      // 这里被 return 掉，新酒店页面上就会留着上一家的标签。
      if (this.rateFilterHotelId && this.rateFilterHotelId !== _hotelId) {
        this.CLEAR_RATE_FILTER_STATE();
      }
      this.rateFilterHotelId = _hotelId;
      // 同一家酒店（切日期/改人数）时不清空：重新拉取期间保留旧选项，
      // 已选筛选项留在界面上，等新数据回来再按“是否仍存在”裁剪
      // 新一轮查询：清掉上一轮的 asyncJob 分片累积
      this.RESET_RATE_FILTER_CHAIN();

      // 请求令牌：上一家酒店的在途响应（含 asyncJob 分片）不能再落到新酒店上
      const requestToken = ++this.roomListRequestToken;

      this.roomListInitLoading = true;
      this.roomListInitLoadingDone = false;
      this.roomList = [];

      const params = {
        preferred: {
          currency: userStore.currency,
          language: userStore.languageISO639Style,
          version: "v1"
        },
        stay: this.searchModel.stay,
        hotel: { _id: _hotelId }
      };

      getHotelRatesAsync(
        params,
        res => {
          if (requestToken !== this.roomListRequestToken) return;

          // 分片返回：并集累积，且只有最后一片（没有 asyncJobId）才允许裁剪已选
          this.SET_RATE_FILTER_CHUNK({
            rateFilter: get(res, "_biz.rateFilter"),
            complete: !res.asyncJobId
          });

          const newRooms = res.rooms || [];
          let changed = false;

          const newList = newRooms.map(newItem => {
            const oldItem = this.roomList.find(r => r._id === newItem._id);

            if (!oldItem) {
              changed = true;
              return { ...newItem, __isNew: true };
            }

            if (!isEqual(oldItem, newItem)) {
              changed = true;
              return { ...newItem, __isUpdated: true };
            }

            return { ...oldItem, __isSame: true };
          });

          if (changed) {
            this.roomList = newList;
          }

          if (!this.roomListInitLoadingDone) {
            this.roomListInitLoading = false;
            this.roomListInitLoadingDone = true;
          }
        },
        () => {
          if (requestToken !== this.roomListRequestToken) return;
          this.roomListInitLoading = false;
          this.roomListInitLoadingDone = true;
        }
      );
    },

    initHotelQuery(query) {
      if (query.checkIn) {
        this.searchModel.stay.date.checkIn = query.checkIn;
      }
      if (query.checkOut) {
        this.searchModel.stay.date.checkOut = query.checkOut;
      }
      if (query.numberOfRooms) {
        this.searchModel.stay.guest.numberOfRooms = Number(query.numberOfRooms);
      }
      if (query.numberOfAdults) {
        this.searchModel.stay.guest.numberOfAdults = Number(
          query.numberOfAdults
        );
      }

      this.resetPagination();
    },
    async getSearchHistory() {
      const historyToggle = useToggles("Y_WEB_SEARCH_HISTORY");
      if (!historyToggle.value) {
        this.historyList = [];
        return;
      }

      const { user } = useUserStoreHook();
      if (user) {
        try {
          const response = await getUserRecentHistory({
            type: "searchUniversal"
          });
          this.historyList = response?.results || [];
        } catch (error) {
          this.historyList = [];
        }
      } else {
        this.historyList = getLocalHistory();
      }
    },
    async setSearchHistory(payload) {
      const { user } = useUserStoreHook();
      if (user) {
        try {
          await setUserRecentHistory(payload);
          this.getSearchHistory();
        } catch (error) {
          console.warn("记录【API】搜索历史失败:", error);
        }
      } else {
        addLocalHistoryItem(payload);
        this.historyList = getLocalHistory();
      }
    },
    async deleteHistory() {
      const { user } = useUserStoreHook();
      if (user) {
        try {
          await clearUserRecentHistory({
            type: "searchUniversal"
          });
          this.historyList = [];
        } catch (error) {
          console.warn("记录【API】搜索历史失败:", error);
        }
      } else {
        clearLocalHistory();
        this.historyList = [];
      }
    }
  },
  getters: {
    preferred() {
      const userStore = useUserStoreHook();
      return userStore.preferred;
    },
    duration() {
      const { checkIn, checkOut } = this.searchModel.stay.date || {};
      if (!checkIn || !checkOut) return 0;
      const checkInDate = dayjs(checkIn);
      const checkOutDate = dayjs(checkOut);
      return checkOutDate
        .startOf("day")
        .diff(checkInDate.startOf("day"), "day");
    }
  }
});

export function useHotelStoreHook() {
  return useHotelStore(store);
}
